import React from 'react';
import {SubnavSectionVariantOptions} from '../../elements/subnav/SubnavSection';

import {
    CenteredTextBlock,
    CenteredTextBlockProps,
    CmsComponentMapper,
    CmsDynamicArray,
    DividedListProps,
    DividedListWithContainer,
    DownloadSection,
    ImageAndTextCard,
    ImageAndTextCardProps,
    LargeQuote,
    LargeQuoteProps,
    LeftAlignedTextBlock,
    LeftAlignedTextBlockProps,
    LineTextBlockProps,
    LineTextBlockWithContainer,
    NewsletterSection,
    NewsletterSectionProps,
    ProductPromoSection,
    ProductPromoSectionProps,
    SectionIntro,
    SectionIntroProps,
    SectionIntroWithCards,
    SectionIntroWithCardsProps,
    SubnavSection,
    SubnavSectionConfigProps,
    VerticalSpacer,
    EmbeddedVideo,
    EmbeddedVideoProps,
    VideoAndTextCard,
    VideoAndTextCardProps,
    ImageCompareTextCard,
    ImageCompareTextCardProps,
    ArticleCopy,
    ArticleMetaCopy,
    ArticleCopyProps,
    ArticleMetaCopyProps
} from '../../elements';
import {DownloadVideoAndNewsletterSplit} from '../../elements/newsletter/DownloadVideoAndNewsletterSplit';
import {
    CardCarousel,
    CardCarouselProps,
    CaseStudySection,
    CaseStudySectionProps,
    CommunityGallery,
    CommunityGalleryProps,
    FAQSection,
    FAQSectionProps,
    FeaturesCarouselSection,
    FeaturesCarouselSectionProps,
    FeaturesGrid,
    FeaturesGridProps,
    LicensingSection,
    LicensingSectionProps,
    MultiCard,
    MultiCardProps,
    OverviewGridSection,
    OverviewGridSectionProps,
    People,
    PeopleProps,
    PluginsSection,
    PluginsSectionProps,
    PromoSection,
    PromoSectionProps,
    SplitListSection,
    SplitListSectionProps
} from '../index';

import {LogoList, LogoListProps} from '../../elements';
import {determineSectionNameSupport} from './dynamicSectionHelper';

export type CmsDynamicSectionOptions =
    | CardCarouselProps
    | CaseStudySectionProps
    | CenteredTextBlockProps
    | CommunityGalleryProps
    | DividedListProps
    | FAQSectionProps
    | FeaturesCarouselSectionProps
    | FeaturesGridProps
    | ImageAndTextCardProps
    | ImageCompareTextCardProps
    | LargeQuoteProps
    | LeftAlignedTextBlockProps
    | LicensingSectionProps
    | LineTextBlockProps
    | LogoListProps
    | MultiCardProps
    | NewsletterSectionProps
    | OverviewGridSectionProps
    | PeopleProps
    | PluginsSectionProps
    | ProductPromoSectionProps
    | PromoSectionProps
    | SectionIntroProps
    | SectionIntroWithCardsProps
    | SplitListSectionProps
    | EmbeddedVideoProps
    | VideoAndTextCardProps
    | ArticleCopyProps
    | ArticleMetaCopyProps;

export type CmsDynamicSectionOptionsArray = CmsDynamicArray<CmsDynamicSectionOptions> | undefined;
export interface CmsDynamicSectionProps {
    hidden?: boolean;
    items?: CmsDynamicSectionOptionsArray;
    sectionConfig: SubnavSectionConfigProps;
    hideTopSectionPadding?: boolean;
    variant?: SubnavSectionVariantOptions;
    needsThemeVerticalSpacer?: boolean;
    hideItemSeperator?: boolean;
}

/**
 * Items that we should implement here:
 * Newsletter (both variants)
 * Product Promo
 */
export const CmsDynamicSection = (props: CmsDynamicSectionProps): JSX.Element | null => {
    const {
        hidden,
        items = [],
        sectionConfig,
        hideTopSectionPadding,
        variant = '',
        needsThemeVerticalSpacer,
        hideItemSeperator = false
    } = props;
    const {accentColor = 'sky'} = sectionConfig;

    if (hidden) return null;

    const additionalProps = {accentColor, sectionConfig, dynamicSection: true};
    const firstComponentCanHandleSectionName = determineSectionNameSupport(items);

    return (
        <SubnavSection
            sectionConfig={sectionConfig}
            hideTopSectionPadding={hideTopSectionPadding}
            hideSectionName={firstComponentCanHandleSectionName}
            hideBottomSectionPadding={!needsThemeVerticalSpacer}
            variant={variant}>
            <CmsComponentMapper
                itemSeparator={hideItemSeperator ? undefined : <VerticalSpacer variant="section" />}
                dynamicPageDataArray={items}
                elementMapping={{
                    articleCopy: <ArticleCopy />,
                    articleMetaCopy: <ArticleMetaCopy />,
                    cardCarousel: <CardCarousel {...additionalProps} />,
                    caseStudy: <CaseStudySection {...additionalProps} />,
                    caseStudyNoIntro: (
                        <CaseStudySection {...additionalProps} hideIntro={true} hideLogos={true} />
                    ),
                    communityGallery: <CommunityGallery {...additionalProps} />,
                    download: <DownloadSection {...additionalProps} />,
                    faq: <FAQSection {...additionalProps} />,
                    featuresCarousel: <FeaturesCarouselSection {...additionalProps} />,
                    featuresGrid: <FeaturesGrid {...additionalProps} />,
                    imageAndTextCard: <ImageAndTextCard {...additionalProps} />,
                    imageCompareTextCard: <ImageCompareTextCard {...additionalProps} />,
                    videoAndTextCard: <VideoAndTextCard {...additionalProps} />,
                    introWithCards: <SectionIntroWithCards {...additionalProps} />,
                    largeQuote: <LargeQuote {...additionalProps} />,
                    licensing: (
                        <LicensingSection
                            {...additionalProps}
                            hideBottomSectionPadding={!needsThemeVerticalSpacer}
                        />
                    ),
                    mediaListConfig: (
                        <DividedListWithContainer
                            {...additionalProps}
                            variant="offset-description"
                        />
                    ),
                    logoList: <LogoList />,
                    multiCard: <MultiCard {...additionalProps} />,
                    newsletter: <NewsletterSection {...additionalProps} />,
                    overviewGrid: <OverviewGridSection {...additionalProps} />,
                    people: <People {...additionalProps} />,
                    productPromo: <ProductPromoSection {...additionalProps} />,
                    promo: <PromoSection {...additionalProps} />,
                    plugins: <PluginsSection {...additionalProps} />,
                    sectionIntroWithBackground: (
                        <SectionIntro {...sectionConfig} {...additionalProps} />
                    ),
                    splitList: <SplitListSection {...additionalProps} />,
                    textBlockLeftAligned: <LeftAlignedTextBlock {...additionalProps} />,
                    textBlockCenterAligned: <CenteredTextBlock {...additionalProps} />,
                    textBlockLine: <LineTextBlockWithContainer {...additionalProps} />,
                    embeddedVideo: <EmbeddedVideo {...additionalProps} />,
                    downloadVideoNewsletter: (
                        <DownloadVideoAndNewsletterSplit
                            {...additionalProps}
                            downloadSectionVariant="video"
                        />
                    )
                }}
            />
        </SubnavSection>
    );
};
